// Página de un evento (/eventos/<id>): portada con su ambiente, datos, "Lo que viví", "No te lo pierdas",
// tip de Jenny, WhatsApp y los otros eventos.
import React, { useEffect, useRef } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { eventoPorId, otrosEventos, precargarEvento, RUTA_EVENTOS, tituloPestana, TITULO_SITIO, waEvento } from '../../services/eventos';
import useEnVista from '../../hooks/useEnVista';
import useVisto from '../../hooks/useVisto';
import { mini } from '../../utils';
import Portada from './Portadas';
import { Boleto, Enfasis, Estrellas, Fuego, Nieve, Polaroid, Pulsera, TiraFotos } from './Piezas';
import './Eventos.css';

/** @typedef {import('../../data/eventos').Evento} Evento */
/** @typedef {import('./Piezas').CargaFoto} CargaFoto */

/**
 * Collage de "Lo que viví" en escritorio (desde 1100 px): [ancho %, giro°, x %, y %] de cada foto en su caja (800 × 720).
 * @type {Object<string, Array<[number, number, number, number]>>}
 */
const COLLAGE = {
  'oogie-boogie-bash': [[37.5, -5, 2.5, 5.6], [47.5, 4, 45, 1.4], [31, -2, 50, 52]],
  'not-so-scary': [[50, -3, 0, 2.8], [31.25, 5, 55, 0], [30, -4, 17.5, 52.8], [31.25, 3, 56.25, 51.4]],
  'food-and-wine': [[37.5, -4, 1.25, 4.2], [27.5, 5, 45, 0], [27.5, -3, 73.75, 8.3], [27.5, 4, 52.5, 56]],
  'pixar-fest': [[50, -3, 0, 2.8], [30, 5, 55, 0], [29.5, -4, 18, 54], [40, 3, 55, 54.2]],
  'very-merry': [[25, -6, 0, 4.2], [32.5, 3, 26.25, 0], [41.25, -3, 58.75, 5.6], [25, 4, 7.5, 50], [27.5, -4, 37.5, 54.2]]
};

/** @param {{ evento: Evento }} props */
function Datos({ evento }) {
  /** [ícono, etiqueta, valor] @type {Array<[string, string, string]>} */
  const filas = [['fa-solid fa-location-dot', 'Dónde', evento.lugar], ['fa-regular fa-calendar', 'Cuándo', evento.cuando], ['fa-solid fa-ticket', 'Boleto', evento.boleto]];
  return (
    <section className="ev-datos" aria-label="Datos del evento">
      <div className="wrap ev-datos__in">
        {filas.map(([icono, etiqueta, valor]) => (
          <div key={etiqueta} className="ev-dato" data-reveal>
            <span className="ev-dato__icono"><i className={icono} /></span>
            <span className="ev-dato__txt"><span className="ev-dato__etiqueta">{etiqueta}</span><span className="ev-dato__valor">{valor}</span></span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * "Lo que viví": collage y tira de más fotos. Sus fotos se piden cuando la sección está a 100 px de la pantalla (al
 * abrir la página queda de 138 a 430 px abajo, salvo en pantallas muy altas): con red lenta, Chrome adelanta las fotos
 * diferidas que están a menos de ~2500 px y le quitan ancho de banda a la foto de la portada.
 * @param {{ evento: Evento }} props
 */
function Vivi({ evento }) {
  const lugares = COLLAGE[evento.id];
  const ref = useRef(null);
  const cerca = useVisto(ref, '100px 0px');
  /** @type {CargaFoto} */
  const carga = cerca ? 'diferida' : 'pendiente';
  return (
    <section ref={ref} id="vivi" className="ev-vivi sec">
      <div className="wrap ev-vivi__in">
        <div className="ev-vivi__txt" data-reveal>
          <p className="eyebrow">Lo que viví</p>
          <h2 className="h2"><Enfasis texto={evento.viviTitulo} /></h2>
          <p className="lead">{evento.viviTexto}</p>
          <p className="ev-vivi__nota">Fui en {evento.anio}<svg viewBox="0 0 56 30" aria-hidden="true"><path d="M2 8c14 18 32 20 48 10" /><path d="M42 12l8 6-9 4" /></svg></p>
        </div>
        <div className={`ev-collage ev-collage--${evento.fotos.length}`}>
          {evento.id === 'food-and-wine' && <p className="ev-collage__nota">Y EPCOT, el escenario de siempre</p>}
          {evento.fotos.map((f, i) => {
            const [w, g, x, y] = lugares[i];
            return (
              <div key={f.src} className="ev-collage__foto" style={{ '--w': `${w}%`, '--x': `${x}%`, '--y': `${y}%`, '--giro': `${g}deg` }}>
                <Polaroid foto={f} carga={carga} />
              </div>
            );
          })}
        </div>
      </div>
      {evento.masFotos && <div className="wrap"><TiraFotos masFotos={evento.masFotos} id={evento.id} carga={carga} /></div>}
    </section>
  );
}

/** @param {{ evento: Evento }} props */
function Imperdibles({ evento }) {
  const ref = useRef(null);
  const visible = useEnVista(ref);
  const t = evento.tema;
  const nieve = evento.ambiente === 'navidad';
  // la nieve se pausa fuera de pantalla y se apaga con "reducir movimiento", como las portadas
  const animada = nieve ? ` ev-anim${visible ? '' : ' ev-pausa'}` : '';
  return (
    <section ref={ref} className={`ev-imperdibles sec${t.oscuro ? ' ev-imperdibles--noche on-dark' : ''}${animada}`} style={{ '--ev-fondo2': t.fondo2, '--ev-texto': t.texto, '--ev-suave': t.suave, '--ev-acento': t.oscuro ? t.acento : t.oscuroAcento, '--ev-claro': t.claro }}>
      {nieve && <Nieve n={16} semilla={51} className="ev-solo-escritorio" />}
      <div className="wrap">
        <div data-reveal>
          <p className="eyebrow">No te lo pierdas</p>
          <h2 className="h2"><Enfasis texto={evento.imperdiblesTitulo} /></h2>
        </div>
        <div className="ev-imperdibles__grid">
          {evento.imperdibles.map((it) => (
            <div key={it.nombre} className="ev-imperdible" data-reveal>
              <span className="ev-imperdible__icono"><i className={it.icono} /></span>
              <div className="ev-imperdible__txt">
                <h3>{it.nombre}</h3>
                <p>{it.texto}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** @param {{ evento: Evento }} props */
function Tip({ evento }) {
  return (
    <section className="ev-tip" aria-label="Tip de Jenny">
      <div className="wrap" data-reveal>
        <div className="ev-tip__tarjeta">
          <span className="ev-cinta" aria-hidden="true" />
          <img src={mini('images/jenny-perfil.jpg')} alt="Jenny Jiménez Pons" loading="lazy" decoding="async" />
          <div className="ev-tip__txt">
            <p className="ev-tip__titulo">Tip de Jenny</p>
            <p className="ev-tip__texto">{evento.tip}</p>
            <p className="ev-tip__firma">— Jenny</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** @param {{ evento: Evento }} props */
function Cta({ evento }) {
  const ref = useRef(null);
  const visible = useEnVista(ref);
  return (
    <section ref={ref} className={`ev-cta on-dark ev-anim${visible ? '' : ' ev-pausa'}`}>
      <Estrellas n={30} semilla={61} />
      <div className="ev-cta__deco ev-solo-escritorio" aria-hidden="true">
        <Pulsera evento={evento} className="ev-pulsera--suelta ev-pulsera--grande ev-flota" style={{ '--giro': '-9deg', left: '4%', top: '34%', animationDelay: '-1s' }} />
        <Fuego x="78%" y="20%" color="var(--acc)" retraso={-0.6} largo={40} />
        <Fuego x="18%" y="76%" color={evento.tema.banda} retraso={-2.2} largo={32} />
      </div>
      <div className="wrap ev-cta__in">
        <div className="ev-cta__txt">
          <p className="eyebrow">¿Te animas?</p>
          <h2 className="h2"><Enfasis texto={evento.ctaTitulo} /></h2>
          <p className="lead">{evento.ctaTexto}</p>
          <div className="ev-cta__accion">
            <a className="btn-wa" href={waEvento(evento)} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" />Cotiza por WhatsApp</a>
            <span>Te respondo personalmente.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** @param {{ evento: Evento }} props */
function Otros({ evento }) {
  const ref = useRef(null);
  const visto = useVisto(ref, '300px 0px');
  // quien llega al final de la página tal vez abra otro evento: sus portadas se descargan de antemano
  useEffect(() => {
    if (visto) otrosEventos(evento.id).forEach(precargarEvento);
  }, [visto, evento.id]);
  return (
    <section ref={ref} className="ev-otros sec">
      <div className="wrap">
        <div className="ev-otros__cabeza" data-reveal>
          <div>
            <p className="eyebrow">Eventos Disney</p>
            <h2 className="h2">Más noches <em>mágicas</em></h2>
          </div>
          <Link to={RUTA_EVENTOS} className="ev-enlace">Ver todos los eventos<i className="fa-solid fa-arrow-right" /></Link>
        </div>
        <div className="ev-otros__lista">
          {otrosEventos(evento.id).map((e) => <Boleto key={e.id} evento={e} />)}
        </div>
      </div>
    </section>
  );
}

export default function EventoPagina() {
  const { id } = useParams();
  const evento = eventoPorId(id || '');
  useEffect(() => {
    if (!evento) return undefined;
    const antes = document.title;
    document.title = tituloPestana(evento);
    return () => { document.title = antes || TITULO_SITIO; };
  }, [evento]);
  if (!evento) return <Navigate to={RUTA_EVENTOS} replace />;
  // tonos del evento para las secciones claras (íconos de los datos, cursivas, "Fui en…" y el tip)
  const tonos = { '--ev-claro': evento.tema.claro, '--ev-osc': evento.tema.oscuroAcento };
  return (
    <main className="ev-pagina" style={tonos}>
      <Portada evento={evento} />
      <Datos evento={evento} />
      <Vivi evento={evento} />
      <Imperdibles evento={evento} />
      <Tip evento={evento} />
      <Cta evento={evento} />
      <Otros evento={evento} />
    </main>
  );
}
